import React, { useEffect, useRef, useMemo } from 'react';
import { User, UserCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import type { TranscriptChunk } from '@/lib/ringBuffer';

interface LiveTranscriptProps {
  chunks: TranscriptChunk[];
  isActive: boolean;
}

export function LiveTranscript({ chunks, isActive }: LiveTranscriptProps) {
  const { theme } = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Group consecutive chunks by speaker for better readability
  const groupedChunks = useMemo(() => {
    const groups: Array<{ speaker: 'agent' | 'customer'; texts: string[]; timestamp: string }> = [];
    
    chunks.forEach((chunk) => {
      const lastGroup = groups[groups.length - 1];
      
      if (lastGroup && lastGroup.speaker === chunk.speaker) {
        // Add to existing group if same speaker
        lastGroup.texts.push(chunk.text);
      } else {
        // Create new group for different speaker
        groups.push({
          speaker: chunk.speaker,
          texts: [chunk.text],
          timestamp: chunk.timestamp
        });
      }
    });
    
    return groups;
  }, [chunks]);

  // Auto-scroll to bottom when new chunks arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chunks]);

  if (!isActive && chunks.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6" style={{
        backgroundColor: theme.colors.bgCard
      }}>
        <div className="text-sm text-center" style={{ color: theme.colors.textSecondary }}>
          Live transcript will appear here during calls
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{
      backgroundColor: theme.colors.bgCard
    }}>
      {/* Header */}
      <div className="px-4 py-3 flex-shrink-0" style={{
        borderBottom: `1px solid ${theme.colors.borderColor}`
      }}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>
            Live Transcript
          </h3>
          {isActive && (
            <span className="flex items-center gap-1.5 text-xs" style={{ color: theme.colors.successText }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: theme.colors.successText }}></span>
              Recording
            </span>
          )}
        </div>
      </div>

      {/* Transcript Messages */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
      >
        {groupedChunks.length === 0 ? (
          <div className="text-sm text-center pt-8" style={{ color: theme.colors.textSecondary }}>
            {isActive ? 'Listening...' : 'Start a call to see live transcript'}
          </div>
        ) : (
          groupedChunks.map((group, idx) => (
            <div key={idx} className={`flex gap-3 ${group.speaker === 'agent' ? 'flex-row' : 'flex-row'}`}>
              {/* Avatar */}
              <div className="flex-shrink-0 mt-1">
                {group.speaker === 'agent' ? (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.infoBg }}>
                    <User className="w-4 h-4" style={{ color: theme.colors.infoText }} />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.successBg }}>
                    <UserCircle className="w-4 h-4" style={{ color: theme.colors.successText }} />
                  </div>
                )}
              </div>

              {/* Message Bubble */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold" style={{ color: theme.colors.textPrimary }}>
                    {group.speaker === 'agent' ? 'Agent' : 'Customer'}
                  </span>
                  <span className="text-xs" style={{ color: theme.colors.textTertiary }}>
                    {new Date(group.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div 
                  className="rounded-lg px-3 py-2 text-sm"
                  style={{
                    backgroundColor: group.speaker === 'agent' ? theme.colors.infoBg : theme.colors.successBg,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${group.speaker === 'agent' ? theme.colors.infoBorder : theme.colors.successBorder}`
                  }}
                >
                  {group.texts.join(' ')}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
